import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import {
  fmtB,
  fmtDate,
  type ApprovalEvent,
  type ApprovalEventType,
  type LvaCondition,
  type LvaCountLineListRow,
  type LvaCountStatus,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { ApprovalTimeline } from '../components/approval/ApprovalTimeline';
import { AuditLog } from '../components/approval/AuditLog';
import { DecisionModal, type Decision } from '../components/approval/DecisionModal';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import {
  useActiveCompanyId,
  useAddCountExtra,
  useApproveCountStep,
  useBranches,
  useClassifyCountLine,
  useCountApprovalEvents,
  useLvaCountExceptions,
  useLvaCountRound,
  useRejectCountRound,
  useReturnCountRound,
  useScanCountLine,
  useSubmitCountRound,
} from '../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../lib/xlsx';

import type {
  LvaCountApprovalEvent,
  LvaCountExceptionRow,
  LvaCountRoundDetail,
  LvaCountVarianceTally,
} from '../lib/queries/lva-count';

// The detail endpoint spreads the round-list-row shape (scope display names
// resolved server-side) onto LvaCountRoundDetail; the hook's interface only
// declares the FK ids, so read the optional resolved names through this widening.
type RoundWithScopeNames = LvaCountRoundDetail & {
  scopeBranchName?: string | null;
  scopeDepartmentName?: string | null;
  scopeCategoryName?: string | null;
};

// LVA Phase C — physical-count round-detail screen (C9). Reuses the /lva
// prototype styling (tbl-shell / tbl / badge / btn). The counter scans (or
// enters) codes to mark lines FOUND, manually classifies variances
// (MISSING / DAMAGED+condition / MOVED+location), and records unknown codes as
// NOT_IN_REGISTER extras. Blind by default: the expected location/custodian are
// masked until a supervisor reveals them (D3), reducing confirmation bias.
// The close flow (submit → engine approval panel) lands in C10/C11.
export const Route = createFileRoute('/lva/count/$roundId')({
  component: LvaCountRoundPage,
});

// Per-status display metadata (label + badge tone), 100%-fidelity to the
// register/lifecycle status badges.
const COUNT_STATUS_META: Readonly<Record<LvaCountStatus, { l: string; c: string }>> = {
  PENDING: { l: 'รอนับ', c: 'neutral' },
  FOUND: { l: 'พบ', c: 'positive' },
  MISSING: { l: 'ไม่พบ', c: 'negative' },
  MOVED: { l: 'ย้ายที่', c: 'warning' },
  DAMAGED: { l: 'ชำรุด', c: 'warning' },
  NOT_IN_REGISTER: { l: 'นอกทะเบียน', c: 'warning' },
};

const CONDITION_LABEL: Readonly<Record<LvaCondition, string>> = {
  NEW: 'ใหม่',
  GOOD: 'ดี',
  FAIR: 'พอใช้',
  POOR: 'ทรุดโทรม',
  DAMAGED: 'ชำรุด',
};

// The four variance chips (PENDING/FOUND are progress, not variance) shown in
// the summary strip + their badge tone.
const TALLY_CHIPS: readonly { key: keyof LvaCountVarianceTally; label: string; tone: string }[] = [
  { key: 'PENDING', label: 'รอนับ', tone: '#52525b' },
  { key: 'FOUND', label: 'พบ', tone: '#117a3d' },
  { key: 'MISSING', label: 'ไม่พบ', tone: '#b42318' },
  { key: 'MOVED', label: 'ย้ายที่', tone: '#b54708' },
  { key: 'DAMAGED', label: 'ชำรุด', tone: '#b54708' },
  { key: 'NOT_IN_REGISTER', label: 'นอกทะเบียน', tone: '#a16207' },
];

function scopeLabel(r: LvaCountRoundDetail): string {
  const w = r as RoundWithScopeNames;
  const parts = [w.scopeBranchName, w.scopeDepartmentName, w.scopeCategoryName].filter(
    (x): x is string => !!x,
  );
  return parts.length ? parts.join(' · ') : 'ทุก Cost Center';
}

export function LvaCountRoundPage() {
  const { roundId } = useParams({ from: '/lva/count/$roundId' });
  const companyId = useActiveCompanyId();
  const roundQuery = useLvaCountRound(companyId, roundId);
  const round = roundQuery.data;

  // Blind count (D3): hide the expected location/custodian until the supervisor
  // reveals them. Off (blind) by default.
  const [revealExpected, setRevealExpected] = useState(false);

  return (
    <div className="page">
      <PageHead crumbs={['ทรัพย์สินมูลค่าต่ำ', 'รอบตรวจนับ']} title="รอบตรวจนับ LVA" />

      {roundQuery.isLoading && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <span className="muted">กำลังโหลด…</span>
        </div>
      )}

      {!roundQuery.isLoading && !round && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <span className="muted">ไม่พบรอบตรวจนับ {roundId}</span>
        </div>
      )}

      {round && (
        <CountRoundDetail
          round={round}
          companyId={companyId}
          revealExpected={revealExpected}
          onToggleReveal={() => setRevealExpected((v) => !v)}
        />
      )}
    </div>
  );
}

function CountRoundDetail({
  round,
  companyId,
  revealExpected,
  onToggleReveal,
}: {
  round: LvaCountRoundDetail;
  companyId: string | undefined;
  revealExpected: boolean;
  onToggleReveal: () => void;
}) {
  const isCounting = round.status === 'COUNTING';
  const tally = round.tally;
  const counted = round.lines.length - tally.PENDING;
  const progress = round.lines.length > 0 ? Math.round((counted / round.lines.length) * 100) : 0;

  // The engine close panel is shown once submitted for approval (Design-Z dual
  // status: round.status PENDING_APPROVAL while approvalStatus PENDING). Final
  // approval flips the round to CLOSED (which locks the grid — isCounting false).
  const pendingApproval = round.approvalStatus === 'PENDING';

  return (
    <>
      <RoundHeader round={round} progress={progress} counted={counted} />

      <VarianceChips tally={tally} />

      {isCounting && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <ScanBox round={round} companyId={companyId} />
          <AddExtraBox round={round} companyId={companyId} />
        </div>
      )}

      {isCounting && <SubmitCloseBar round={round} companyId={companyId} tally={tally} />}

      {pendingApproval && <CountApprovalPanel round={round} companyId={companyId} />}

      <CountLinesGrid
        round={round}
        companyId={companyId}
        isCounting={isCounting}
        revealExpected={revealExpected}
        onToggleReveal={onToggleReveal}
      />

      <ExceptionWorklist companyId={companyId} />
    </>
  );
}

// ============================================================
// C11 — exception worklist + Excel export
// ============================================================

// The display label for a count result, shared by the export and the worklist
// (the same mapping the grid badges use).
function countStatusLabel(s: LvaCountStatus): string {
  return COUNT_STATUS_META[s].l;
}

// A short human-readable variance summary per line for the export "variance"
// column — where (MOVED) / how (DAMAGED) / what kind (extra) the line differs.
function varianceCell(ln: LvaCountLineListRow): string {
  switch (ln.countStatus) {
    case 'MISSING':
      return 'ขาดบัญชี (ไม่พบของจริง)';
    case 'MOVED': {
      const where = [ln.foundBranchName, ln.foundLocation].filter(Boolean).join(' · ');
      return where ? `ย้ายไป ${where}` : 'ย้ายที่';
    }
    case 'DAMAGED':
      return ln.conditionFound ? `ชำรุด (${CONDITION_LABEL[ln.conditionFound]})` : 'ชำรุด';
    case 'NOT_IN_REGISTER':
      return 'เกินบัญชี (พบของจริงนอกทะเบียน)';
    case 'FOUND':
    case 'PENDING':
    default:
      return '';
  }
}

// "Export Excel" over the round's count lines via lib/xlsx exportRows. Columns:
// code / name / expected location / result (countStatus) / variance / note —
// the auditor's count-sheet, one row per snapshotted item (+ extras).
function exportRoundLines(round: LvaCountRoundDetail): void {
  const cols: ExportColumn<LvaCountLineListRow>[] = [
    { header: 'รหัส', value: (ln) => ln.snapCode, width: 18 },
    { header: 'ชื่อทรัพย์สิน', value: (ln) => ln.snapName, width: 32 },
    { header: 'ตำแหน่งคาดหวัง', value: (ln) => ln.snapLocation ?? '', width: 22 },
    { header: 'ผลการนับ', value: (ln) => countStatusLabel(ln.countStatus), width: 14 },
    { header: 'ผลต่าง', value: (ln) => varianceCell(ln), width: 28 },
    { header: 'หมายเหตุ', value: (ln) => ln.note ?? '', width: 28 },
  ];
  void exportRows(`PhysicalCount_${round.roundNo}_${todayStamp()}.xlsx`, round.roundNo, cols, [
    ...round.lines,
  ]);
}

// Cross-round worklist of still-OPEN variances (unresolved MISSING /
// NOT_IN_REGISTER from useLvaCountExceptions) — the follow-up queue for the
// asset team. Lines clear from here once the owning round is approved and the
// resolution movement is posted (resolved=true).
function ExceptionWorklist({ companyId }: { companyId: string | undefined }) {
  const exceptionsQuery = useLvaCountExceptions(companyId);
  const exceptions = exceptionsQuery.data ?? [];

  return (
    <div className="tbl-shell" style={{ marginTop: 16 }}>
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          <Icon name="bell" size={13} /> ผลต่าง / ข้อยกเว้นที่ยังไม่ปิด ({exceptions.length})
        </span>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
          ไม่พบ (ขาดบัญชี) + นอกทะเบียน (เกินบัญชี) ทุกรอบที่ยังรอติดตาม
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>รอบ</th>
            <th>รหัส</th>
            <th>ชื่อทรัพย์สิน</th>
            <th>ผลการนับ</th>
            <th>ตำแหน่งคาดหวัง</th>
            <th>หมายเหตุ</th>
          </tr>
        </thead>
        <tbody>
          {exceptions.length === 0 && (
            <tr>
              <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 18 }}>
                ไม่มีผลต่างค้างติดตาม
              </td>
            </tr>
          )}
          {exceptions.map((ex) => (
            <ExceptionRow key={ex.id} ex={ex} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExceptionRow({ ex }: { ex: LvaCountExceptionRow }) {
  const meta = COUNT_STATUS_META[ex.countStatus];
  return (
    <tr>
      <td className="muted" style={{ fontSize: 12 }}>
        {ex.round.roundNo}
      </td>
      <td className="code">
        <b>{ex.snapCode}</b>
      </td>
      <td>{ex.snapName}</td>
      <td>
        <span className={`badge ${meta.c}`}>{meta.l}</span>
      </td>
      <td className="muted">{ex.snapLocation ?? '—'}</td>
      <td className="muted" style={{ fontSize: 12 }}>
        {ex.note ?? '—'}
      </td>
    </tr>
  );
}

// Close-submit bar (C10) — visible while COUNTING. "ส่งปิดรอบ" submits the round
// to the central approval engine (docType PC). Disabled until every line is
// classified (no PENDING line); the server re-enforces the same guard. The
// engine amount-bands on Σ cost(MISSING), so a clean count routes through the
// lightest tier.
function SubmitCloseBar({
  round,
  companyId,
  tally,
}: {
  round: LvaCountRoundDetail;
  companyId: string | undefined;
  tally: LvaCountVarianceTally;
}) {
  const submitMut = useSubmitCountRound(companyId);
  const [error, setError] = useState<string | null>(null);
  const hasPending = tally.PENDING > 0;
  const noLines = round.lines.length === 0;
  const disabled = hasPending || noLines || submitMut.isPending || !companyId;

  const submit = () => {
    setError(null);
    submitMut.mutate(
      { roundId: round.id },
      {
        onError: (e: unknown) => setError(e instanceof Error ? e.message : 'ส่งปิดรอบไม่สำเร็จ'),
      },
    );
  };

  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div className="muted" style={{ fontSize: 13 }}>
        {hasPending
          ? `ยังเหลือ ${tally.PENDING} รายการที่ยังไม่ได้จำแนกผล — จำแนกให้ครบก่อนส่งปิดรอบ`
          : 'จำแนกผลครบทุกรายการแล้ว พร้อมส่งปิดรอบเพื่ออนุมัติ (4-eyes)'}
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
        {error && <span style={{ fontSize: 13, color: 'var(--negative)' }}>{error}</span>}
        <button className="btn primary" onClick={submit} disabled={disabled}>
          <Icon name="check" size={14} /> {submitMut.isPending ? 'กำลังส่งปิดรอบ…' : 'ส่งปิดรอบ'}
        </button>
      </div>
    </div>
  );
}

// Engine close panel (C10) — rendered while the round is PENDING_APPROVAL. Reuses
// the shared ApprovalTimeline + AuditLog (Component 7) and the document-agnostic
// DecisionModal, exactly like cip/payreq. The "พิจารณา" button opens the modal;
// approve drives useApproveCountStep (the final-step approval CLOSES the round and
// auto-applies the line resolutions server-side), return/reject drive the
// matching hooks. SoD/role are enforced server-side (the engine is the gate).
function CountApprovalPanel({
  round,
  companyId,
}: {
  round: LvaCountRoundDetail;
  companyId: string | undefined;
}) {
  const approveMut = useApproveCountStep(companyId);
  const rejectMut = useRejectCountRound(companyId);
  const returnMut = useReturnCountRound(companyId);
  const eventsQuery = useCountApprovalEvents(companyId, round.id);
  const [showDecide, setShowDecide] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentStep = round.currentStep ?? 1;
  const totalSteps = round.approvals.length;
  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const auditEvents = useMemo(
    () => (eventsQuery.data ?? []).map(toApprovalEvent),
    [eventsQuery.data],
  );

  const handleDecide = (d: Decision) => {
    setError(null);
    const onSuccess = () => setShowDecide(false);
    const onError = (e: unknown) => setError(e instanceof Error ? e.message : 'ดำเนินการไม่สำเร็จ');
    const sod = d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {};
    if (d.action === 'approve') {
      approveMut.mutate(
        { roundId: round.id, step: d.step, comment: d.comment, ...sod },
        { onSuccess, onError },
      );
    } else if (d.action === 'return') {
      returnMut.mutate(
        { roundId: round.id, step: d.step, reason: d.reason, ...sod },
        { onSuccess, onError },
      );
    } else {
      rejectMut.mutate(
        { roundId: round.id, step: d.step, reason: d.reason, ...sod },
        { onSuccess, onError },
      );
    }
  };

  return (
    <div className="card" style={{ marginTop: 16, padding: '16px 20px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>สายอนุมัติปิดรอบ — {round.roundNo}</div>
        <span className="badge warning">
          <span className="bdot" /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </span>
      </div>

      {round.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg, #fef3e8)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {round.returnedReason}
        </div>
      )}

      <ApprovalTimeline
        approvals={round.approvals.map((a) => ({
          id: a.stepNo,
          stepNo: a.stepNo,
          roleLabel: a.roleLabel,
          approvedBy: a.approvedBy,
          comment: a.comment,
          approvedAt: a.approvedAt,
        }))}
        isActive
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          round.submittedBy ? (
            <div className="meta">
              ส่งปิดรอบโดย {round.submittedBy}
              {round.submittedAt ? ` · ${fmtDate(round.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      <div style={{ marginTop: 16 }}>
        <AuditLog events={auditEvents} />
      </div>

      <div
        style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {error && <span style={{ fontSize: 13, color: 'var(--negative)' }}>{error}</span>}
        <button className="btn primary" onClick={() => setShowDecide(true)} disabled={decideBusy}>
          <Icon name="check" size={14} /> พิจารณา
        </button>
      </div>

      {showDecide && (
        <DecisionModal
          voucherNo={round.roundNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={error}
          titlePrefix="พิจารณาการปิดรอบตรวจนับ"
          stepLabel={(s) => `ขั้นที่ ${s}/${totalSteps}`}
          onConfirm={handleDecide}
          onClose={() => {
            setShowDecide(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
}

// Map a count engine event (the lean LvaCountApprovalEvent the API returns) into
// the shared AuditLog's ApprovalEvent shape. The panel only reads id/createdAt/
// eventType/stepNo/actorName/reason — comment is the count engine's reason field.
function toApprovalEvent(e: LvaCountApprovalEvent): ApprovalEvent {
  return {
    id: e.id,
    companyId: '',
    docType: e.docType,
    docId: e.docId,
    eventType: e.eventType as ApprovalEventType,
    stepNo: e.stepNo,
    actorId: e.actorId,
    actorName: e.actorName ?? '—',
    reason: e.comment,
    createdAt: e.createdAt,
  } as unknown as ApprovalEvent;
}

function RoundHeader({
  round,
  progress,
  counted,
}: {
  round: LvaCountRoundDetail;
  progress: number;
  counted: number;
}) {
  const statusBadge = (() => {
    if (round.status === 'CLOSED')
      return (
        <span className="badge positive">
          <Icon name="check" size={10} /> ปิดรอบแล้ว
        </span>
      );
    if (round.status === 'PENDING_APPROVAL')
      return (
        <span className="badge warning">
          <span className="bdot" /> รออนุมัติ
        </span>
      );
    return (
      <span className="badge warning">
        <span className="bdot" /> กำลังนับ
      </span>
    );
  })();

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)' }}>
            {round.roundNo}
          </div>
          <div className="muted" style={{ fontSize: 14, marginTop: 2 }}>
            {round.period} · วันตรวจตามแผน {fmtDate(String(round.plannedDate))}
          </div>
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          Scope: {scopeLabel(round)}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              นับแล้ว {counted}/{round.lines.length} ({progress}%)
            </div>
            <div
              style={{
                width: 160,
                height: 6,
                background: 'var(--border)',
                borderRadius: 4,
                overflow: 'hidden',
                marginTop: 4,
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: progress === 100 ? 'var(--positive)' : 'var(--brand, #1a56a6)',
                }}
              />
            </div>
          </div>
          {statusBadge}
        </div>
      </div>
    </div>
  );
}

function VarianceChips({ tally }: { tally: LvaCountVarianceTally }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {TALLY_CHIPS.map((c) => (
        <div
          key={c.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: 8,
            minWidth: 96,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.tone }} />
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.label}</span>
          <span style={{ marginLeft: 'auto', fontSize: 18, fontWeight: 700, color: c.tone }}>
            {tally[c.key]}
          </span>
        </div>
      ))}
    </div>
  );
}

// Scan / enter-code box → useScanCountLine. On an unknown-code miss the server
// returns a "not in this round" error; we surface a prompt to add it as an extra.
function ScanBox({
  round,
  companyId,
}: {
  round: LvaCountRoundDetail;
  companyId: string | undefined;
}) {
  const scanMut = useScanCountLine(companyId);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [missCode, setMissCode] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const c = code.trim();
    if (!c) return;
    setMsg(null);
    setMissCode(null);
    scanMut.mutate(
      { roundId: round.id, code: c },
      {
        onSuccess: (lineRow: LvaCountLineListRow) => {
          setMsg({ tone: 'ok', text: `พบ ${lineRow.snapCode} — ${lineRow.snapName}` });
          setCode('');
        },
        onError: (err: unknown) => {
          // A code not in this round's snapshot → offer add-extra.
          setMissCode(c);
          setMsg({
            tone: 'err',
            text: err instanceof Error ? err.message : 'ไม่พบรหัสนี้ในรอบตรวจนับ',
          });
        },
      },
    );
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
        <Icon name="search" size={14} /> สแกน / กรอกรหัสเพื่อตรวจนับ
      </div>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8 }}>
        <input
          className="input"
          placeholder="สแกน QR หรือกรอกรหัสทรัพย์สิน"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ flex: 1, height: 38, fontSize: 14 }}
          autoFocus
        />
        <button className="btn primary" type="submit" disabled={scanMut.isPending}>
          ตรวจนับ
        </button>
      </form>
      {msg && (
        <div
          style={{
            marginTop: 10,
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 13,
            background:
              msg.tone === 'ok' ? 'var(--positive-tint, #ecfdf3)' : 'var(--negative-tint, #fef2f2)',
            color: msg.tone === 'ok' ? 'var(--positive)' : 'var(--negative)',
          }}
        >
          {msg.text}
          {missCode && (
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
              รหัส {missCode} ไม่อยู่ในรอบนี้ — ใช้ "เพิ่มรายการนอกทะเบียน"
              เพื่อบันทึกเป็นของส่วนเกิน
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Add an unknown / not-in-register code as a NOT_IN_REGISTER extra line.
function AddExtraBox({
  round,
  companyId,
}: {
  round: LvaCountRoundDetail;
  companyId: string | undefined;
}) {
  const addMut = useAddCountExtra(companyId);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [foundLocation, setFoundLocation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (!code.trim()) return setError('กรุณาระบุรหัส/บาร์โค้ด');
    if (!name.trim()) return setError('กรุณาระบุชื่อทรัพย์สิน');
    addMut.mutate(
      {
        roundId: round.id,
        data: {
          code: code.trim(),
          name: name.trim(),
          ...(foundLocation.trim() ? { foundLocation: foundLocation.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          setCode('');
          setName('');
          setFoundLocation('');
          setOpen(false);
        },
        onError: (e: unknown) =>
          setError(e instanceof Error ? e.message : 'เพิ่มรายการนอกทะเบียนล้มเหลว'),
      },
    );
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: open ? 10 : 0, gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          <Icon name="plus" size={14} /> ของส่วนเกิน (พบแต่ไม่อยู่ในทะเบียน)
        </span>
        {!open && (
          <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={() => setOpen(true)}>
            เพิ่มรายการนอกทะเบียน
          </button>
        )}
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            className="input"
            placeholder="รหัส/บาร์โค้ด"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ height: 36, fontSize: 14 }}
          />
          <input
            className="input"
            placeholder="ชื่อทรัพย์สิน"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ height: 36, fontSize: 14 }}
          />
          <input
            className="input"
            placeholder="ตำแหน่งที่พบ (ถ้ามี)"
            value={foundLocation}
            onChange={(e) => setFoundLocation(e.target.value)}
            style={{ height: 36, fontSize: 14 }}
          />
          {error && <div style={{ color: 'var(--negative)', fontSize: 12 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn ghost sm" onClick={() => setOpen(false)}>
              ยกเลิก
            </button>
            <button className="btn primary sm" onClick={submit} disabled={addMut.isPending}>
              บันทึกรายการเพิ่ม
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CountLinesGrid({
  round,
  companyId,
  isCounting,
  revealExpected,
  onToggleReveal,
}: {
  round: LvaCountRoundDetail;
  companyId: string | undefined;
  isCounting: boolean;
  revealExpected: boolean;
  onToggleReveal: () => void;
}) {
  return (
    <div className="tbl-shell" style={{ marginTop: 16 }}>
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>รายการตรวจนับ ({round.lines.length})</span>
        <button
          className="btn sm ghost"
          style={{ marginLeft: 'auto' }}
          onClick={onToggleReveal}
          title="โหมดนับแบบปิดบัง (Blind): ซ่อนตำแหน่ง/ผู้ครอบครองที่คาดหวังจนกว่าจะจำแนกผล"
        >
          <Icon name="eye" size={12} />{' '}
          {revealExpected ? 'ซ่อนข้อมูลคาดหวัง' : 'แสดงตำแหน่งคาดหวัง'}
        </button>
        <button
          className="btn sm ghost"
          onClick={() => exportRoundLines(round)}
          disabled={round.lines.length === 0}
          title="ส่งออกรายการตรวจนับเป็นไฟล์ Excel"
        >
          <Icon name="download" size={12} /> Export Excel
        </button>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อทรัพย์สิน</th>
            <th>ตำแหน่งคาดหวัง</th>
            <th>ผู้ครอบครอง</th>
            <th className="num">ราคาทุน</th>
            <th>ผลการนับ</th>
            {isCounting && <th>จำแนกผล</th>}
          </tr>
        </thead>
        <tbody>
          {round.lines.length === 0 && (
            <tr>
              <td
                colSpan={isCounting ? 7 : 6}
                className="muted"
                style={{ textAlign: 'center', padding: 18 }}
              >
                ไม่มีรายการในรอบนี้
              </td>
            </tr>
          )}
          {round.lines.map((ln) => (
            <CountLineRow
              key={ln.id}
              line={ln}
              roundId={round.id}
              companyId={companyId}
              isCounting={isCounting}
              revealExpected={revealExpected}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountLineRow({
  line,
  roundId,
  companyId,
  isCounting,
  revealExpected,
}: {
  line: LvaCountLineListRow;
  roundId: string;
  companyId: string | undefined;
  isCounting: boolean;
  revealExpected: boolean;
}) {
  const meta = COUNT_STATUS_META[line.countStatus];
  // Blind: an unclassified PENDING line hides its expected location/custodian
  // until revealed (D3). Once classified, the values are always shown.
  const classified = line.countStatus !== 'PENDING';
  const showExpected = revealExpected || classified;
  const isExtra = line.lvaItemId == null;

  return (
    <tr>
      <td className="code">
        <b>{line.snapCode}</b>
        {isExtra && (
          <span className="badge warning" style={{ marginLeft: 6, fontSize: 11 }}>
            นอกทะเบียน
          </span>
        )}
      </td>
      <td>{line.snapName}</td>
      <td className="muted">{showExpected ? (line.snapLocation ?? '—') : '••••'}</td>
      <td className="muted">{showExpected ? (line.snapCustodianName ?? '—') : '••••'}</td>
      <td className="num">{line.snapCost != null ? fmtB(Number(line.snapCost)) : '—'}</td>
      <td>
        <span className={`badge ${meta.c}`}>{meta.l}</span>
        {line.countStatus === 'MOVED' && line.foundLocation && (
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            → {line.foundBranchName ? `${line.foundBranchName} ` : ''}
            {line.foundLocation}
          </div>
        )}
        {line.countStatus === 'DAMAGED' && line.conditionFound && (
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {CONDITION_LABEL[line.conditionFound]}
          </div>
        )}
      </td>
      {isCounting && (
        <td>
          <ClassifyControl line={line} roundId={roundId} companyId={companyId} />
        </td>
      )}
    </tr>
  );
}

// Per-row classify control: quick FOUND/MISSING buttons + a "more" popover for
// DAMAGED(+condition) and MOVED(+location/branch) which need extra fields.
function ClassifyControl({
  line,
  roundId,
  companyId,
}: {
  line: LvaCountLineListRow;
  roundId: string;
  companyId: string | undefined;
}) {
  const classifyMut = useClassifyCountLine(companyId);
  const branchesQuery = useBranches(companyId);
  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data]);
  const [mode, setMode] = useState<null | 'DAMAGED' | 'MOVED'>(null);
  const [conditionFound, setConditionFound] = useState<LvaCondition>('DAMAGED');
  const [foundLocation, setFoundLocation] = useState('');
  const [foundBranchId, setFoundBranchId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Extras (NOT_IN_REGISTER) are not register-classifiable here.
  if (line.lvaItemId == null) {
    return (
      <span className="muted" style={{ fontSize: 12 }}>
        —
      </span>
    );
  }

  const apply = (status: LvaCountStatus, extra?: Record<string, unknown>) => {
    setError(null);
    classifyMut.mutate(
      { roundId, lineId: line.id, data: { countStatus: status, ...(extra ?? {}) } },
      {
        onSuccess: () => setMode(null),
        onError: (e: unknown) => setError(e instanceof Error ? e.message : 'บันทึกผลการนับล้มเหลว'),
      },
    );
  };

  const labelStyle: CSSProperties = {
    fontSize: 12,
    color: 'var(--text-muted)',
    display: 'block',
    marginBottom: 4,
  };

  if (mode === 'DAMAGED') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
        <label style={labelStyle}>สภาพที่พบ</label>
        <select
          className="input"
          value={conditionFound}
          onChange={(e) => setConditionFound(e.target.value as LvaCondition)}
          style={{ height: 32, fontSize: 13 }}
          aria-label="สภาพที่พบ"
        >
          {(Object.keys(CONDITION_LABEL) as LvaCondition[]).map((c) => (
            <option key={c} value={c}>
              {CONDITION_LABEL[c]}
            </option>
          ))}
        </select>
        {error && <div style={{ color: 'var(--negative)', fontSize: 11 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn ghost sm" onClick={() => setMode(null)}>
            ยกเลิก
          </button>
          <button
            className="btn primary sm"
            onClick={() => apply('DAMAGED', { conditionFound })}
            disabled={classifyMut.isPending}
          >
            บันทึกชำรุด
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'MOVED') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
        <label style={labelStyle}>สาขาที่พบ (ถ้าย้ายสาขา)</label>
        <SearchSelect
          value={foundBranchId}
          onChange={setFoundBranchId}
          placeholder="— สาขาเดิม —"
          options={branches.map((b) => ({ value: b.id, label: `${b.code} ${b.nameTh}` }))}
          getLabel={(v) => {
            const b = branches.find((x) => x.id === v);
            return b ? `${b.code} ${b.nameTh}` : '';
          }}
          ariaLabel="สาขาที่พบ"
        />
        <input
          className="input"
          placeholder="ตำแหน่งที่พบ"
          value={foundLocation}
          onChange={(e) => setFoundLocation(e.target.value)}
          style={{ height: 32, fontSize: 13 }}
        />
        {error && <div style={{ color: 'var(--negative)', fontSize: 11 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn ghost sm" onClick={() => setMode(null)}>
            ยกเลิก
          </button>
          <button
            className="btn primary sm"
            onClick={() => {
              if (!foundBranchId && !foundLocation.trim())
                return setError('ระบุสาขาหรือตำแหน่งที่พบ');
              apply('MOVED', {
                ...(foundBranchId ? { foundBranchId } : {}),
                ...(foundLocation.trim() ? { foundLocation: foundLocation.trim() } : {}),
              });
            }}
            disabled={classifyMut.isPending}
          >
            บันทึกย้ายที่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <button
        className="btn sm"
        style={{ height: 26, fontSize: 12 }}
        onClick={() => apply('FOUND')}
        disabled={classifyMut.isPending}
      >
        พบ
      </button>
      <button
        className="btn sm"
        style={{ height: 26, fontSize: 12, color: 'var(--negative)' }}
        onClick={() => apply('MISSING')}
        disabled={classifyMut.isPending}
      >
        ไม่พบ
      </button>
      <button
        className="btn sm"
        style={{ height: 26, fontSize: 12 }}
        onClick={() => setMode('MOVED')}
      >
        ย้ายที่
      </button>
      <button
        className="btn sm"
        style={{ height: 26, fontSize: 12 }}
        onClick={() => setMode('DAMAGED')}
      >
        ชำรุด
      </button>
    </div>
  );
}
